import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import { format } from 'date-fns';
import { de, enUS } from 'date-fns/locale';
import { Ticket, Plus, Copy, Check, User, Warning, PencilSimple, Trash, X } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';

const AdminVouchers = () => {
  const { t, language, API } = useAuth();
  const dateLocale = language === 'de' ? de : enUS;
  const L = (en, deu) => language === 'de' ? deu : en;
  
  const [vouchers, setVouchers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingVoucher, setEditingVoucher] = useState(null);
  const [copiedCode, setCopiedCode] = useState(null);
  const [formData, setFormData] = useState({
    code: '', amount: '', remaining_balance: '', description: '',
    user_id: '', voucher_type: 'credit', payment_method: 'cash',
    legacy_import: false, legacy_source: '',
  });

  // Customer search
  const [vCustSearch, setVCustSearch] = useState('');
  const [vCustResults, setVCustResults] = useState([]);
  const [vCustSearching, setVCustSearching] = useState(false);
  const [vSelectedCustName, setVSelectedCustName] = useState('');
  const [vShowCustDropdown, setVShowCustDropdown] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);

  useEffect(() => { loadVouchers(); }, []);

  const loadVouchers = async () => {
    try { setLoading(true); const { data } = await API.get('/vouchers'); setVouchers(data); }
    catch (e) { console.error(e); } finally { setLoading(false); }
  };

  // Debounced customer search
  useEffect(() => {
    if (vCustSearch.length < 2) { setVCustResults([]); return; }
    const timer = setTimeout(async () => {
      setVCustSearching(true);
      try {
        const { data } = await API.get(`/users/search?q=${encodeURIComponent(vCustSearch)}&limit=20`);
        setVCustResults(data);
        setVShowCustDropdown(true);
      } catch (e) { console.error(e); }
      finally { setVCustSearching(false); }
    }, 300);
    return () => clearTimeout(timer);
  }, [vCustSearch]);

  const generateCode = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let code = '';
    for (let i = 0; i < 8; i++) { if (i === 4) code += '-'; code += chars.charAt(Math.floor(Math.random() * chars.length)); }
    setFormData({ ...formData, code });
  };

  const resetForm = () => setFormData({ code: '', amount: '', remaining_balance: '', description: '', user_id: '', voucher_type: 'credit', payment_method: 'cash', legacy_import: false, legacy_source: '' });

  const openCreate = () => { resetForm(); setEditingVoucher(null); setShowModal(true); };
  
  const openEdit = (voucher) => {
    setEditingVoucher(voucher);
    setFormData({
      code: voucher.code,
      amount: String(voucher.amount),
      remaining_balance: String(voucher.remaining_balance ?? voucher.amount),
      description: voucher.description || '',
      user_id: voucher.user_id || '',
      voucher_type: voucher.voucher_type || 'credit',
      payment_method: 'cash',
    });
    setShowModal(true);
  };

  const handleSubmit = async () => {
    try {
      if (editingVoucher) {
        await API.put(`/vouchers/${editingVoucher.id}`, {
          code: formData.code,
          amount: parseFloat(formData.amount),
          remaining_balance: parseFloat(formData.remaining_balance),
          description: formData.description || null,
          voucher_type: formData.voucher_type,
          user_id: formData.user_id || '',
        });
        toast.success(L('Voucher updated!', 'Gutschein aktualisiert!'));
      } else {
        await API.post('/vouchers', {
          ...formData,
          amount: parseFloat(formData.amount),
          user_id: formData.user_id || null,
          payment_method: formData.payment_method,
          legacy_import: !!formData.legacy_import,
          legacy_source: formData.legacy_import ? (formData.legacy_source || 'Playtomic (imported)') : null,
        });
        toast.success(formData.legacy_import
          ? L('Legacy voucher added (not on cash register)', 'Legacy-Gutschein erfasst (nicht in Kassa)')
          : L('Voucher created and added to cash register!', 'Gutschein erstellt und in Kasse erfasst!'));
      }
      setShowModal(false); resetForm(); setEditingVoucher(null); loadVouchers();
    } catch (error) {
      toast.error(error.response?.data?.detail || L('Error', 'Fehler'));
    }
  };

  const handleDelete = async (voucher) => {
    setConfirmAction({
      message: L(`Delete voucher ${voucher.code}?`, `Gutschein ${voucher.code} löschen?`),
      onConfirm: async () => {
        try {
          await API.delete(`/vouchers/${voucher.id}`);
          toast.success(L('Voucher deleted', 'Gutschein gelöscht'));
          loadVouchers();
        } catch (e) { toast.error(L('Error', 'Fehler')); }
        setConfirmAction(null);
      }
    });
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    toast.success(L('Code copied!', 'Code kopiert!'));
    setTimeout(() => setCopiedCode(null), 2000);
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-vouchers">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-1">{t('vouchers')}</h1>
          <p className="text-slate-500">{L('Create and manage vouchers & training packages', 'Gutscheine und Trainingspakete erstellen')}</p>
        </div>
        <Button onClick={openCreate} className="btn-primary flex items-center gap-2" data-testid="create-voucher-btn">
          <Plus size={18} weight="bold" /> {L('Create Voucher', 'Gutschein erstellen')}
        </Button>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map(i => <div key={i} className="h-48 bg-slate-100 animate-pulse rounded-2xl" />)}
        </div>
      ) : vouchers.length === 0 ? (
        <div className="card-static p-12 text-center">
          <Ticket size={48} className="text-slate-300 mx-auto mb-4" weight="duotone" />
          <p className="text-slate-500 mb-4">{L('No vouchers yet', 'Keine Gutscheine vorhanden')}</p>
          <Button onClick={openCreate} className="btn-primary">{L('Create First Voucher', 'Ersten Gutschein erstellen')}</Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {vouchers.map((voucher) => {
            const isTraining = voucher.voucher_type === 'training';
            const remaining = voucher.remaining_balance ?? voucher.amount;
            const used = voucher.amount - remaining;
            const pct = voucher.amount > 0 ? (remaining / voucher.amount) * 100 : 0;
            const isLow = pct <= 20 && pct > 0;
            const isEmpty = remaining <= 0;

            return (
              <div key={voucher.id} className="card-static p-6" data-testid={`voucher-card-${voucher.id}`}>
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`px-2 py-0.5 text-[10px] font-bold uppercase rounded-full ${isTraining ? 'bg-emerald-50 text-emerald-600' : 'bg-blue-50 text-blue-600'}`}>
                        {isTraining ? 'Training' : 'Credit'}
                      </span>
                      {voucher.legacy_import && (
                        <span
                          className="px-2 py-0.5 text-[10px] font-bold uppercase rounded-full bg-amber-100 text-amber-700"
                          title={voucher.legacy_source || 'Legacy import (nicht in Kassa)'}
                          data-testid={`legacy-badge-${voucher.id}`}
                        >
                          {L('Legacy', 'Legacy')}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xl font-black text-emerald-600 font-mono">{voucher.code}</span>
                      <button onClick={() => copyCode(voucher.code)} className="text-slate-400 hover:text-slate-700">
                        {copiedCode === voucher.code ? <Check size={16} className="text-emerald-500" /> : <Copy size={16} />}
                      </button>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button onClick={() => openEdit(voucher)} className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors" data-testid={`edit-voucher-${voucher.id}`}>
                      <PencilSimple size={16} />
                    </button>
                    <button onClick={() => handleDelete(voucher)} className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors" data-testid={`delete-voucher-${voucher.id}`}>
                      <Trash size={16} />
                    </button>
                  </div>
                </div>

                {voucher.user_name && (
                  <div className="flex items-center gap-2 mb-3 bg-slate-50 p-2 rounded-lg">
                    <User size={16} className="text-slate-400" />
                    <span className="text-sm text-slate-700 font-medium">{voucher.user_name}</span>
                  </div>
                )}

                {isTraining && (
                  <div className="mb-3">
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-slate-500">{L('Remaining', 'Restbetrag')}</span>
                      <span className={`font-bold ${isEmpty ? 'text-red-500' : isLow ? 'text-amber-500' : 'text-emerald-600'}`}>{remaining.toFixed(2)} / {voucher.amount.toFixed(2)} CHF</span>
                    </div>
                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div className={`h-full rounded-full transition-all ${isEmpty ? 'bg-red-400' : isLow ? 'bg-amber-400' : 'bg-emerald-400'}`} style={{ width: `${pct}%` }} />
                    </div>
                    {isEmpty && <p className="text-red-500 text-xs mt-1 flex items-center gap-1"><Warning size={12} /> {L('Voucher depleted!', 'Gutschein aufgebraucht!')}</p>}
                    {isLow && !isEmpty && <p className="text-amber-500 text-xs mt-1 flex items-center gap-1"><Warning size={12} /> {L('Almost empty', 'Fast aufgebraucht')}</p>}
                  </div>
                )}

                <div className="space-y-2 mb-4">
                  <div className="flex justify-between"><span className="text-slate-500 text-sm">{L('Value', 'Wert')}</span><span className="text-emerald-600 font-bold">{voucher.amount} CHF</span></div>
                  {(voucher.remaining_balance ?? voucher.amount) !== voucher.amount && (
                    <div className="flex justify-between"><span className="text-slate-500 text-sm">{L('Remaining', 'Restbetrag')}</span><span className="text-slate-800 font-semibold">{(voucher.remaining_balance ?? voucher.amount).toFixed(2)} CHF</span></div>
                  )}
                  {voucher.user_name && <div className="flex justify-between"><span className="text-slate-500 text-sm">{L('Owner', 'Besitzer')}</span><span className="text-slate-800">{voucher.user_name}</span></div>}
                </div>
                {voucher.description && <p className="text-slate-500 text-sm italic">&ldquo;{voucher.description}&rdquo;</p>}
              </div>
            );
          })}
        </div>
      )}

      {/* Create / Edit Voucher Modal */}
      <Dialog open={showModal} onOpenChange={(open) => { if (!open) { setShowModal(false); setEditingVoucher(null); } }}>
        <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-md max-h-[90vh] overflow-y-auto" data-testid="voucher-modal">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">{editingVoucher ? L('Edit Voucher', 'Gutschein bearbeiten') : L('New Voucher', 'Neuer Gutschein')}</DialogTitle>
            <DialogDescription className="text-slate-500">{editingVoucher ? L('Update voucher details', 'Gutscheindetails aktualisieren') : L('Create a voucher or training package', 'Gutschein oder Trainingspaket erstellen')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {/* Voucher Type — Training packages were replaced by Abos.
                Only credit vouchers remain. */}

            {/* Code */}
            <div className="space-y-2">
              <Label className="label-text">Code *</Label>
              <div className="flex gap-2">
                <Input value={formData.code} onChange={(e) => setFormData({...formData, code: e.target.value.toUpperCase()})}
                  className="bg-slate-50 border-slate-200 font-mono uppercase" placeholder="XXXX-XXXX" data-testid="voucher-code" />
                <Button type="button" onClick={generateCode} variant="outline" className="whitespace-nowrap border-slate-200">
                  {L('Generate', 'Generieren')}
                </Button>
              </div>
            </div>

            {/* Amount */}
            <div className="space-y-2">
              <Label className="label-text">{L('Total Amount (CHF)', 'Gesamtbetrag (CHF)')} *</Label>
              <Input type="number" value={formData.amount} onChange={(e) => setFormData({...formData, amount: e.target.value})}
                className="bg-slate-50 border-slate-200" placeholder="50" data-testid="voucher-amount" />
            </div>

            {/* Link to User */}
            <div className="space-y-2">
              <Label className="label-text">{L('Link to Customer', 'Kunde zuweisen')}</Label>
              <div className="relative">
                <Input
                  value={formData.user_id ? vSelectedCustName : vCustSearch}
                  onChange={(e) => {
                    if (formData.user_id) { setFormData({...formData, user_id: ''}); setVSelectedCustName(''); }
                    setVCustSearch(e.target.value);
                    if (e.target.value.length < 2) setVShowCustDropdown(false);
                  }}
                  onFocus={() => { if (vCustResults.length > 0) setVShowCustDropdown(true); }}
                  className={`bg-slate-50 border-slate-200 ${formData.user_id ? 'text-emerald-700 font-semibold bg-emerald-50 border-emerald-300' : ''}`}
                  placeholder={L('Search customer (optional)...', 'Kunde suchen (optional)...')}
                  data-testid="voucher-user"
                />
                {formData.user_id && (
                  <button onClick={() => { setFormData({...formData, user_id: ''}); setVSelectedCustName(''); setVCustSearch(''); }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"><X size={16} /></button>
                )}
                {vShowCustDropdown && vCustResults.length > 0 && !formData.user_id && (
                  <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-xl shadow-lg max-h-48 overflow-y-auto">
                    {vCustResults.map(u => (
                      <button key={u.id} onClick={() => {
                        setFormData({...formData, user_id: u.id});
                        setVSelectedCustName(`${u.name} (${u.email})`);
                        setVCustSearch(''); setVShowCustDropdown(false);
                      }}
                      className="w-full text-left px-3 py-2 hover:bg-emerald-50 text-sm transition-colors border-b border-slate-50 last:border-0">
                        <span className="font-medium text-slate-800">{u.name}</span>
                        <span className="text-slate-400 ml-2 text-xs">{u.email}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label className="label-text">{L('Description', 'Beschreibung')}</Label>
              <Input value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})}
                className="bg-slate-50 border-slate-200" placeholder={L('e.g. Training Package 10 lessons', 'z.B. Trainingspaket 10 Lektionen')} />
            </div>

            {!editingVoucher && (
              <div className={`space-y-2 rounded-xl border p-3 transition-all ${
                formData.legacy_import ? 'border-amber-200 bg-amber-50' : 'border-slate-200 bg-slate-50'
              }`} data-testid="legacy-import-block">
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    className="mt-0.5 w-4 h-4 accent-amber-600"
                    checked={!!formData.legacy_import}
                    onChange={(e) => setFormData({ ...formData, legacy_import: e.target.checked })}
                    data-testid="legacy-import-toggle"
                  />
                  <div>
                    <div className="text-sm font-semibold text-slate-900">
                      {L('Legacy Playtomic voucher', 'Legacy-Gutschein (Playtomic)')}
                    </div>
                    <div className="text-[11px] text-slate-500 leading-relaxed mt-0.5">
                      {L(
                        'Skip the cash register — this voucher was already paid on the previous system. It will NOT appear in today\'s Tagesabrechnung.',
                        'Kassa überspringen — dieser Gutschein wurde bereits auf dem alten System bezahlt. Er erscheint NICHT in der heutigen Tagesabrechnung.'
                      )}
                    </div>
                  </div>
                </label>
                {formData.legacy_import && (
                  <Input
                    value={formData.legacy_source}
                    onChange={(e) => setFormData({ ...formData, legacy_source: e.target.value })}
                    className="bg-white border-amber-200 text-xs"
                    placeholder={L('Source note (e.g. Playtomic 2024, invoice #123)', 'Herkunft (z.B. Playtomic 2024, Rechnung #123)')}
                    data-testid="legacy-source-input"
                  />
                )}
              </div>
            )}

            {!editingVoucher && !formData.legacy_import && (
              <div className="space-y-2">
                <Label className="label-text">{L('Payment', 'Zahlung')}</Label>
                <div className="grid grid-cols-3 gap-2">
                  <button type="button" onClick={() => setFormData({...formData, payment_method: 'cash'})}
                    className={`p-2.5 border rounded-xl text-sm font-semibold transition-all ${formData.payment_method === 'cash' ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : 'border-slate-200 text-slate-600 hover:border-slate-300'}`}
                    data-testid="voucher-pay-cash">
                    {L('Cash', 'Bar')}
                  </button>
                  <button type="button" onClick={() => setFormData({...formData, payment_method: 'card'})}
                    className={`p-2.5 border rounded-xl text-sm font-semibold transition-all ${formData.payment_method === 'card' ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-slate-200 text-slate-600 hover:border-slate-300'}`}
                    data-testid="voucher-pay-card">
                    {L('Card', 'Karte')}
                  </button>
                  <button type="button" onClick={() => setFormData({...formData, payment_method: 'invoice'})}
                    className={`p-2.5 border rounded-xl text-sm font-semibold transition-all ${formData.payment_method === 'invoice' ? 'border-amber-500 bg-amber-50 text-amber-700' : 'border-slate-200 text-slate-600 hover:border-slate-300'}`}
                    data-testid="voucher-pay-invoice">
                    {L('Invoice', 'Rechnung')}
                  </button>
                </div>
                <p className="text-xs text-slate-500 pt-1">
                  {formData.payment_method === 'invoice'
                    ? L('Marked as INVOICE in the cash register — no cash/card counted today. Book the payment separately when the invoice is settled.', 'Wird als RECHNUNG in der Kasse markiert — heute wird kein Bar/Karten-Betrag gezählt. Zahlung separat verbuchen wenn die Rechnung beglichen wird.')
                    : L('The amount is registered as PAID in the cash register and appears in today\'s daily report.', 'Der Betrag wird sofort als BEZAHLT in der Kasse erfasst und erscheint in der heutigen Tagesabrechnung.')}
                </p>
              </div>
            )}

            <div className="flex gap-3 pt-4">
              <Button variant="outline" onClick={() => { setShowModal(false); setEditingVoucher(null); }} className="flex-1 border-slate-200 text-slate-600">{t('cancel')}</Button>
              <Button onClick={handleSubmit} disabled={!formData.code || !formData.amount}
                className="flex-1 bg-emerald-500 text-white hover:bg-emerald-600 font-bold" data-testid="submit-voucher">
                {editingVoucher ? L('Save Changes', 'Änderungen speichern') : L('Create', 'Erstellen')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Confirm Dialog */}
      <Dialog open={!!confirmAction} onOpenChange={(open) => { if (!open) setConfirmAction(null); }}>
        <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold">{L('Confirm', 'Bestätigen')}</DialogTitle>
          </DialogHeader>
          <p className="text-slate-600 mt-2">{confirmAction?.message}</p>
          <div className="flex gap-3 mt-6">
            <Button variant="outline" onClick={() => setConfirmAction(null)} className="flex-1 border-slate-200 text-slate-600">{L('Cancel', 'Abbrechen')}</Button>
            <Button onClick={() => confirmAction?.onConfirm()} className="flex-1 bg-red-500 text-white hover:bg-red-600 font-bold">
              {L('Delete', 'Löschen')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminVouchers;
